import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseEntityService } from 'src/app/shared/services/base-entity.service';
import { environment } from 'src/environments/environment';
import Response = ZoningAPI.Response;
import PaginatedResponse = ZoningAPI.PaginatedResponse;
import ISystemEventDTO = ZoningAPI.ISystemEventDTO;
import { SystemEventParentType } from 'src/app/shared/enums/system-event-parent-type';
import { SystemEventType } from 'src/app/shared/enums/system-event-type-enum';
import { SystemEvent, SystemEventFactory } from 'src/app/shared/models/system-event';
import { SystemEventMapper } from 'src/app/shared/mappers/system-event-mapper';
import IProcessEventsSequenceAnalyzerStatistic = ZoningAPI.IProcessEventsSequenceAnalyzerStatistic;

const API_ENDPOINT = environment.ZONAR_API_URL + '/system-events';

@Injectable({
    providedIn: 'root'
})
export class SystemEventService extends BaseEntityService<SystemEvent> {
    mapper = new SystemEventMapper();

    constructor(private readonly http: HttpClient) {
        super(SystemEventFactory);
    }

    getSystemEvents(state_env: string, parentId: string, parentType: SystemEventParentType): Observable<SystemEvent[]> {
        if (!state_env || !parentId || !parentType) {
            return of([]);
        }
        const params = new HttpParams({
            fromObject: {
                state_env,
                parentId,
                parentType: String(parentType),
                sortOrder: 'desc'
            }
        });
        return this.http
            .get<Response>(`${API_ENDPOINT}`, { params })
            .pipe(
                map(response => {
                    return (response.data as ISystemEventDTO<any>[]).map(dto =>
                        this.initEntity(this.mapper.fromDTO(dto))
                    );
                })
            );
    }

    getSystemEventsStatistic(
        state_env: string,
        parentId: string,
        parentType: SystemEventParentType
    ): Observable<IProcessEventsSequenceAnalyzerStatistic> {
        if (!state_env || !parentId || !parentType) {
            return of(null);
        }
        const params = new HttpParams({
            fromObject: {
                state_env,
                parentId,
                parentType: String(parentType)
            }
        });
        return this.http
            .get<Response>(`${API_ENDPOINT}/statistic`, { params })
            .pipe(
                map(response => {
                    return response.data as IProcessEventsSequenceAnalyzerStatistic;
                })
            );
    }

    getSystemEventsPaginated(
        state_env: string,
        parentId?: string,
        parentType?: SystemEventParentType,
        eventType?: SystemEventType,
        paginationParams?: { page?: number; limit?: number; getCount?: boolean },
        createdAtStart?: string,
        createdAtEnd?: string
    ): Observable<{ systemEvents: SystemEvent[]; totalCount: number }> {
        if (!state_env) {
            return of({ systemEvents: [], totalCount: 0 });
        }

        const fromObject: any = {
            state_env,
            sortOrder: 'desc'
        };

        if (parentId) {
            fromObject.parentId = parentId;
        }
        if (parentType) {
            fromObject.parentType = String(parentType);
        }

        if (eventType !== undefined && eventType !== SystemEventType.NONE) {
            fromObject.eventType = String(eventType);
        }

        if (createdAtStart) {
            fromObject.createdAtStart = createdAtStart;
        }

        if (createdAtEnd) {
            fromObject.createdAtEnd = createdAtEnd;
        }

        if (paginationParams) {
            if (paginationParams.page !== undefined) {
                fromObject.page = String(paginationParams.page);
            }
            if (paginationParams.limit !== undefined) {
                fromObject.limit = String(paginationParams.limit);
            }
            if (paginationParams.getCount !== undefined) {
                fromObject.getCount = String(paginationParams.getCount);
            }
        }

        const params = new HttpParams({ fromObject });

        return this.http
            .get<PaginatedResponse<ISystemEventDTO<any>[]>>(`${API_ENDPOINT}`, { params })
            .pipe(
                map(response => {
                    const systemEvents = (response.data as ISystemEventDTO<any>[]).map(dto =>
                        this.initEntity(this.mapper.fromDTO(dto))
                    );
                    return {
                        systemEvents,
                        totalCount: response.rows || 0
                    };
                })
            );
    }
}
